/**
 * @file Builds the project menu item that shares the soft commitment form URL.
 */
import React from "react";
import type { MenuProps } from "antd";
import { NavLink, useOutletContext } from "react-router-dom";
import { LinkOutlined } from "@ant-design/icons";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { getProjectScfUrl } from "@components/Investment";

const LABEL_SHARE = "Copy Public URL";
const MESSAGE_COPIED = "Soft commit form URL is copied to the clipboard";

type LinkTemplates = {
  softCommitmentUrl?: string;
}

type Organization = {
  id: string;
  domain: string;
  linkTemplates?: LinkTemplates;
}

type Sponsor = {
  id: string;
  name: string;
}

type OutletContext = {
  organization: Organization;
}

type ScfAppContext = AppContext & {
  isBroker: boolean;
  identity: {
    sponsorId?: string;
  };
}

type MenuItem = Required<MenuProps>["items"][number]


/**
 * Builds the menu item factory that shares the project's soft commitment
 * form URL for the broker or for each sponsor.
 */
const useScfMenuItem = (
  projectId: string,
  sponsors: Sponsor[] = [],
): (() => MenuItem) => {
  const { showSuccessMessage, identity, isBroker } =
    useAppContext() as ScfAppContext;

  // NOTE: use the full organization document from the outlet context (fetched
  //       via ReadMyOrganization) since it carries `linkTemplates`, unlike
  //       `identity.organization`.
  const { organization } = useOutletContext<OutletContext>();

  /**
   * Copies the soft commitment form URL for the given sponsor to the clipboard.
   */
  const copyProjectScfUrl = (
    event: React.MouseEvent<HTMLAnchorElement>,
    sponsorId: string,
  ): void => {
    // NOTE: the menu label is a NavLink for styling only; prevent its default
    //       navigation so clicking just copies the URL.
    event.preventDefault();

    const url = getProjectScfUrl(organization, projectId, sponsorId);

    navigator.clipboard.writeText(url);
    showSuccessMessage(MESSAGE_COPIED);
  };

  /**
   * Builds the share menu item for the broker or for the list of sponsors.
   */
  const getScfMenuItem = (): MenuItem => {
    const { sponsorId } = identity;

    if (isBroker) {
      return {
        key: "share",
        icon: <LinkOutlined />,
        label: (
          <NavLink to="" onClick={event => copyProjectScfUrl(event, sponsorId)}>
            {LABEL_SHARE}
          </NavLink>
        ),
      };
    }

    const sponsorItems = sponsors.map(sponsor => ({
      key: sponsor.id,
      label: (
        <NavLink to="" onClick={event => copyProjectScfUrl(event, sponsor.id)}>
          {sponsor.name}
        </NavLink>
      ),
    }));

    const hasNoSponsors = sponsors.length === 0;

    if (hasNoSponsors) {
      return {
        key: "share",
        icon: <LinkOutlined />,
        label: LABEL_SHARE,
        disabled: true,
      };
    }

    return {
      key: "share",
      icon: <LinkOutlined />,
      label: LABEL_SHARE,
      children: sponsorItems,
    };
  };

  return getScfMenuItem;
};

export default useScfMenuItem;
